import {
    FiActivity,
    FiExternalLink,
    FiEye,
    FiFileText,
    FiGift,
    FiHelpCircle,
    FiHome,
    FiSearch,
} from 'react-icons/fi';
import { MdOutlineSchool, MdPlayLesson } from 'react-icons/md';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
} from '@/ui/design-system/components/ui/sidebar';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import ExtLink from '../ExtLink';
import Link from '../Link';
import { SearchModal } from 'components/Navigation';
import SidebarBrandsMenu from './SidebarBrandsMenu';
import SidebarExperimentsMenu from './SidebarExperimentsMenu';
import SidebarIndustriesMenu from './SidebarIndustriesMenu';
import SidebarTrendsMenu from './SidebarTrendsMenu';
import SidebarUserFooter from './SidebarUserFooter';
import { SpateLogo } from '@/ui/design-system/components/ui/spate-logo';
import useAppStore from 'state/stores/appStore';

const externalLinks = [
    {
        key: 'onboarding',
        icon: MdPlayLesson,
        children: 'Onboarding videos',
        href: 'https://help.spate.nyc/en/article/spate-tutorials',
        mixpanelContext: 'ONBOARDING_VIDEOS',
    },
    {
        key: 'help',
        icon: FiHelpCircle,
        children: 'Help',
        href: 'https://help.spate.nyc',
        mixpanelContext: 'HELP_DOCS',
    },
    {
        key: 'releases',
        icon: FiGift,
        children: 'Product releases',
        href: 'https://spatenyc.notion.site/Release-Log-f031990bf4184e26a1fb94c0a1bf7f9d',
        mixpanelContext: 'WHATS_NEW',
    },
];

function AppSidebar(props) {
    const { state, setOpen } = useSidebar();
    const location = useLocation();
    const expandedFromCollapsed = useAppStore(
        (s) => s.sidebarExpandedFromCollapsed,
    );
    const setExpandedFromCollapsed = useAppStore(
        (s) => s.setSidebarExpandedFromCollapsed,
    );

    const isHome = useRouteMatch({ path: '/', exact: true });
    const isWatchlist = useRouteMatch({ path: '/watchlist' });
    const isAlerts = useRouteMatch({ path: '/alerts' });
    const isReports = useRouteMatch({ path: '/reports' });
    const isSpotlights = useRouteMatch({ path: '/spotlights' });
    const [activeMenu, setActiveMenu] = useState();
    const [searchOpen, setSearchOpen] = useState(false);

    const openSearch = useCallback(() => setSearchOpen(true), []);
    const isMac = useMemo(
        () => /Mac|iPod|iPhone|iPad/.test(navigator.platform),
        [],
    );
    const modifierKey = isMac ? '⌘' : 'Ctrl';

    // Global Cmd+K / Ctrl+K keyboard shortcut
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen((prev) => !prev);
            }
            if (e.key === 'Escape') {
                setSearchOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleMenuToggle = (menuName) => (open) => {
        if (open && state === 'collapsed') {
            setExpandedFromCollapsed(true);
        }
        setActiveMenu(open ? menuName : null);
    };

    // Collapse sidebar before paint if it was temporarily expanded from collapsed state.
    // useLayoutEffect avoids a visible flash of the expanded sidebar on remount.
    useLayoutEffect(() => {
        if (expandedFromCollapsed) {
            setExpandedFromCollapsed(false);
            setOpen(false);
            setActiveMenu(null);
        }
    }, [location]);

    // Reset flag if sidebar collapses for any other reason
    useEffect(() => {
        if (state === 'collapsed') {
            setExpandedFromCollapsed(false);
        }
    }, [state]);

    return (
        <>
            <Sidebar collapsible="icon" {...props}>
                {/* Header: Logo + collapse trigger */}
                <SidebarHeader className="group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:py-3">
                    <SidebarMenu>
                        <SidebarMenuItem className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-1">
                            <SidebarTrigger className="absolute right-2 top-1/2 -translate-y-1/2 group-data-[collapsible=icon]:static group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:!size-8" />
                            <SidebarMenuButton
                                size="lg"
                                asChild
                                className="group-data-[collapsible=icon]:hidden"
                            >
                                <Link to="/" mixpanelContext="Sidebar Logo">
                                    <span className="flex items-center">
                                        <SpateLogo />
                                    </span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarHeader>

                <SidebarContent>
                    {/* Main navigation */}
                    <SidebarGroup>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {/* Search */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        tooltip="Search"
                                        onClick={openSearch}
                                        data-component="sidenav-search-trigger"
                                    >
                                        <FiSearch />
                                        <span>Search</span>
                                        <kbd className="ml-auto pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:inline-flex group-data-[collapsible=icon]:!hidden">
                                            <span className="text-xs">
                                                {modifierKey}
                                            </span>
                                            K
                                        </kbd>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Home */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={!!isHome}
                                        tooltip="Home"
                                    >
                                        <Link
                                            to="/"
                                            mixpanelContext="Main Home Page"
                                        >
                                            <FiHome />
                                            <span>Home</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Trends (collapsible) */}
                                <SidebarTrendsMenu
                                    isOpen={
                                        activeMenu === undefined
                                            ? undefined
                                            : activeMenu === 'trends'
                                    }
                                    onToggle={handleMenuToggle('trends')}
                                />

                                {/* Brands (collapsible) */}
                                <SidebarBrandsMenu
                                    isOpen={
                                        activeMenu === undefined
                                            ? undefined
                                            : activeMenu === 'brands'
                                    }
                                    onToggle={handleMenuToggle('brands')}
                                />

                                {/* My Watchlist */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={!!isWatchlist}
                                        tooltip="My Watchlist"
                                    >
                                        <Link
                                            to="/watchlist"
                                            mixpanelContext="User Watchlist"
                                        >
                                            <FiEye />
                                            <span>My Watchlist</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Alerts */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={!!isAlerts}
                                        tooltip="Alerts"
                                    >
                                        <Link
                                            to="/alerts"
                                            mixpanelContext="Alerts"
                                        >
                                            <FiActivity />
                                            <span>Alerts</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Reports */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={!!isReports}
                                        tooltip="Reports"
                                    >
                                        <Link
                                            to="/reports"
                                            mixpanelContext="Reports"
                                        >
                                            <FiFileText />
                                            <span>Reports</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Spotlight */}
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        asChild
                                        isActive={!!isSpotlights}
                                        tooltip="Spotlight"
                                    >
                                        <Link
                                            to="/spotlights"
                                            mixpanelContext="Spotlight Section"
                                        >
                                            <MdOutlineSchool />
                                            <span>Spotlight</span>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                {/* Experiments (collapsible) */}
                                <SidebarExperimentsMenu
                                    isOpen={
                                        activeMenu === undefined
                                            ? undefined
                                            : activeMenu === 'experiments'
                                    }
                                    onToggle={handleMenuToggle('experiments')}
                                />

                                {/* Industries (collapsible) */}
                                <SidebarIndustriesMenu
                                    isOpen={
                                        activeMenu === undefined
                                            ? undefined
                                            : activeMenu === 'industries'
                                    }
                                    onToggle={handleMenuToggle('industries')}
                                />
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>

                    {/* External links - pushed to bottom */}
                    <SidebarGroup className="mt-auto">
                        <SidebarGroupContent>
                            <SidebarMenu>
                                {externalLinks.map(
                                    ({
                                        key,
                                        icon: Icon,
                                        children,
                                        href,
                                        mixpanelContext,
                                    }) => (
                                        <SidebarMenuItem key={key}>
                                            <SidebarMenuButton
                                                asChild
                                                tooltip={children}
                                                className="no-underline"
                                            >
                                                <ExtLink
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    mixpanelContext={
                                                        mixpanelContext
                                                    }
                                                >
                                                    <Icon />
                                                    <span>{children}</span>
                                                    <FiExternalLink className="ml-auto size-3 text-muted-foreground" />
                                                </ExtLink>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    ),
                                )}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>

                {/* Footer: User info + settings + logout */}
                <SidebarFooter>
                    <SidebarSeparator />
                    <SidebarUserFooter />
                </SidebarFooter>
            </Sidebar>
            <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
        </>
    );
}

export default AppSidebar;
